"use client";
import {
  setAuthTokenSilent,
  useAccount,
  useAccountToken,
  useAuthCall,
} from "@web/lib/auth";
import { makeApiRequest, objectRequest } from "@web/lib/data";
import {
  ReactNode,
  createContext,
  use,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";

const MetadataContext = createContext<{
  private: { [k: string]: any };
  public: { [k: string]: any };
  refresh: () => void;
}>({ private: {}, public: {}, refresh: () => {} });

let subscribers: (() => void)[] = [];
let promise: Promise<{ private: any; public: any } | null> | null = null;
const nullPromise = Promise.resolve(null);

let publicPromise: Promise<any> | null = null;
let privatePromise: Promise<any> | null = null;

function getPromise() {
  let remakePromise = false;
  if (!privatePromise) {
    remakePromise = true;
    privatePromise = makeApiRequest(
      "GET",
      "logged",
      localStorage.getItem("token") ?? undefined,
    )
      .then(objectRequest)
      .then((response) => response.content)
      .catch(() => ({}));
  }

  if (!publicPromise) {
    remakePromise = true;
    publicPromise = makeApiRequest("GET", "public")
      .then(objectRequest)
      .then((response) => response.content)
      .catch(() => ({}));
  }

  if (remakePromise || !promise) {
    promise = Promise.all([privatePromise, publicPromise]).then(
      ([priv, pub]) => {
        return { private: priv, public: pub };
      },
    );
  }

  return promise;
}

const promiseStore = {
  subscribe(onStoreChange: () => void) {
    subscribers.push(onStoreChange);
    return () => {
      subscribers = subscribers.filter(
        (subscriber) => subscriber !== onStoreChange,
      );
    };
  },
  getSnapshot() {
    return getPromise();
  },
  getServerSnapshot() {
    return nullPromise;
  },
};

const useDataCache = new Map<
  string,
  | Promise<{
      content: any;
      pagination?:
        | {
            count: number;
            limit: number;
          }
        | undefined;
    }>
  | {
      content: any;
      pagination?:
        | {
            count: number;
            limit: number;
          }
        | undefined;
    }
>();

const useStaleDataCache = new Map<
  string,
  {
    content: any;
    pagination?:
      | {
          count: number;
          limit: number;
        }
      | undefined;
  }
>();

const dataSubscribers: Map<string, (() => void)[]> = new Map();
const useDataCacheEvictions = new Map<string, ReturnType<typeof setTimeout>>();
const USE_DATA_CACHE_TTL_MS = 2 * 60 * 1000;

function clearUseDataCacheEviction(cacheKey: string | undefined) {
  if (!cacheKey) {
    return;
  }

  const timeout = useDataCacheEvictions.get(cacheKey);
  if (!timeout) {
    return;
  }

  clearTimeout(timeout);
  useDataCacheEvictions.delete(cacheKey);
}

function scheduleUseDataCacheEviction(cacheKey: string | undefined) {
  if (!cacheKey) {
    return;
  }

  clearUseDataCacheEviction(cacheKey);
  useDataCacheEvictions.set(
    cacheKey,
    setTimeout(() => {
      const subscribers = dataSubscribers.get(cacheKey);
      if (subscribers && subscribers.length > 0) {
        return;
      }

      useDataCache.delete(cacheKey);
      useStaleDataCache.delete(cacheKey);
      dataSubscribers.delete(cacheKey);
      useDataCacheEvictions.delete(cacheKey);
    }, USE_DATA_CACHE_TTL_MS),
  );
}

export function refresh() {
  privatePromise = null;
  publicPromise = null;
  for (const subscriber of subscribers) {
    subscriber();
  }
}

export function useMetadataStore() {
  return useSyncExternalStore(
    promiseStore.subscribe,
    promiseStore.getSnapshot,
    promiseStore.getServerSnapshot,
  );
}

export function refetchData(url: string, method: string = "GET") {
  const token = localStorage.getItem("token") ?? "";
  const cacheKey = method + ":" + url + ":" + (token ?? "");
  clearUseDataCacheEviction(cacheKey);

  const promise = makeApiRequest(method, url, token ?? undefined).then(
    objectRequest,
  );

  const staleData = useDataCache.get(cacheKey);
  promise.then((value) => {
    useDataCache.set(cacheKey, value);
    useStaleDataCache.set(cacheKey, value);
    for (const subscriber of dataSubscribers.get(cacheKey) ?? []) {
      subscriber();
    }
  });
  return promise.then(async (value) => ({
    current: value,
    stale: await staleData,
  }));
}

export function invalidateUseDataCache(cacheKey: string | undefined) {
  if (cacheKey) {
    queueMicrotask(() => {
      const oldValue = useDataCache.get(cacheKey);
      if (!oldValue) return;

      if (oldValue instanceof Promise) {
        oldValue.then((value) => useStaleDataCache.set(cacheKey, value));
      } else {
        useStaleDataCache.set(cacheKey, oldValue);
      }

      useDataCache.delete(cacheKey);
      for (const subscriber of dataSubscribers.get(cacheKey) ?? []) {
        subscriber();
      }
    });
  }
}

function dropUseDataCacheKey(cacheKey: string | undefined) {
  scheduleUseDataCacheEviction(cacheKey);
}

export function useData<T = any>(
  url: string,
  method: string = "GET",
  setDataCacheKey?: (cacheKey: string | undefined) => void,
): {
  content: T;
  pagination?:
    | {
        count: number;
        limit: number;
      }
    | undefined;
} {
  const token = useAccountToken();
  const currentCacheKey = method + ":" + url + ":" + (token ?? "");
  const [cacheKey, setCacheKey] = useState(currentCacheKey);

  useEffect(() => {
    if (setDataCacheKey) {
      setDataCacheKey(cacheKey);
    }
  }, [setDataCacheKey, cacheKey]);

  useEffect(() => {
    clearUseDataCacheEviction(cacheKey);
  }, [cacheKey]);

  useEffect(() => {
    if (cacheKey === currentCacheKey) {
      return;
    }
    if (useDataCache.has(currentCacheKey) && cacheKey !== currentCacheKey) {
      setCacheKey(currentCacheKey);
      return;
    }
    const promise = makeApiRequest(method, url, token ?? undefined)
      .then(objectRequest)
      .then((r) => {
        useStaleDataCache.set(cacheKey, r);
        useDataCache.set(cacheKey, r);
        for (const subscriber of dataSubscribers.get(cacheKey) ?? []) {
          subscriber();
        }
        return r;
      });
    const ref = {
      shouldSet: true,
    };
    promise.then((result) =>
      ref.shouldSet
        ? (() => {
            useDataCache.set(currentCacheKey, result);
            setCacheKey(currentCacheKey);
          })()
        : void 0,
    );
    return () => void (ref.shouldSet = false);
  }, [currentCacheKey]);

  const value = useSyncExternalStore(
    useCallback(
      (subscribe) => {
        clearUseDataCacheEviction(cacheKey);
        const subs = dataSubscribers.get(cacheKey) ?? [];
        dataSubscribers.set(cacheKey, [...subs, subscribe]);
        return () => {
          const subs = dataSubscribers.get(cacheKey);
          if (subs) {
            const newSubscribers = subs.filter((item) => item !== subscribe);
            if (newSubscribers.length === 0) {
              dataSubscribers.delete(cacheKey);
              dropUseDataCacheKey(cacheKey);
            } else {
              dataSubscribers.set(cacheKey, newSubscribers);
            }
          } else {
            dataSubscribers.delete(cacheKey);
            dropUseDataCacheKey(cacheKey);
          }
        };
      },
      [cacheKey, token],
    ),
    useCallback(() => {
      const cachedPromise = useDataCache.get(cacheKey);
      const stalePromise = useStaleDataCache.get(cacheKey);

      if (stalePromise && cachedPromise) {
        return stalePromise;
      }

      if (!stalePromise && cachedPromise) {
        return cachedPromise;
      }

      const promise = makeApiRequest(method, url, token ?? undefined)
        .then(objectRequest)
        .then((r) => {
          useStaleDataCache.set(cacheKey, r);
          useDataCache.set(cacheKey, r);
          for (const subscriber of dataSubscribers.get(cacheKey) ?? []) {
            subscriber();
          }
          return r;
        });
      useDataCache.set(cacheKey, promise);

      if (stalePromise) {
        return stalePromise;
      }

      return promise;
    }, [cacheKey, token]),
  );

  if (value instanceof Promise) {
    throw value;
  }
  return value;
}

function usePromiseWithPrevious<T>(promise: Promise<T>) {
  const [previousValue, setPreviousValue] = useState<T | undefined>(undefined);

  useEffect(() => {
    promise.then((value) => {
      setPreviousValue(value);
    });
  }, [promise]);

  if (!previousValue) {
    return use(promise);
  }
  return previousValue;
}

export function MetadataProvider({ children }: { children: ReactNode }) {
  const account = useAccount();
  const promise = useMetadataStore();
  const value = usePromiseWithPrevious(promise);
  const [email, setEmail] = useState(() => account?.email);

  useEffect(() => {
    if (email !== account?.email) {
      refresh();
      setEmail(account?.email);
    }
  }, [email, account?.email]);

  useEffect(() => {
    setAuthTokenSilent(value?.private?.account?.token);
  }, [value?.private?.account?.token]);

  return (
    <MetadataContext.Provider value={{ ...value!, refresh }}>
      {children}
    </MetadataContext.Provider>
  );
}

type PromiseReturn<
  T extends (...args: TArgs) => Promise<TReturn>,
  TArgs extends any[],
  TReturn extends any,
> = T extends (...args: infer TArgs) => Promise<infer TReturn>
  ? (...args: TArgs) => Promise<TReturn>
  : never;

export function useMutation<
  T extends (...args: TArgs) => Promise<TReturn>,
  TArgs extends any[],
  TReturn extends any,
>(fn: T): PromiseReturn<T, TArgs, TReturn> {
  const refresh = useRefreshMetadata();
  return useCallback(
    (...args: TArgs) =>
      fn(...args).then((ret) => {
        queueMicrotask(refresh);
        return ret;
      }),
    [fn, refresh],
  ) as any;
}

export function useRefreshMetadata() {
  return useContext(MetadataContext).refresh;
}

export function useMetadata() {
  return useContext(MetadataContext).private;
}

export function usePublicMetadata() {
  return useContext(MetadataContext).public;
}

export function usePreformPayment(requiredBalance?: number) {
  const authCall = useAuthCall("POST", "payment/session", { requiredBalance });
  return useCallback(async () => {
    const input = await authCall();
    const session = objectRequest(input);
    window.open(session.content.url, "_blank");
  }, [authCall]);
}
